// Schema-aware table for `timeline-marker-v1` registry files
// (Timeline Lane 1.0.0). Used by BOTH the Markers tab (markers.yaml —
// fixed reference markers: Epoch day, Today, 2038 boundary) and the
// Future Windows tab (future-events.yaml — planning windows with
// endDate + shaded display). Both files share the schema; the column
// values key off `marker.display` (line vs window). Consumed at
// runtime by the standalone D3 viz — do not reshape events[].
//
// Columns: Marker (color swatch from marker.color + bold name), Kind
// ("Line" | "Window" chip from marker.display), When (single ISO or
// the literal "Today (dynamic)" for `date: dynamic:today`; windows
// render `<date> – <endDate>`), Scope (marker.scope chip), Label
// (marker.label in mono — what shows on the viz), Tags (chips),
// Description (truncated + full on hover), Source (link, or plain
// sourceLabel text when sourceUrl is absent).

import { useMemo, useState } from 'react';
import yaml from 'js-yaml';
import { validateRegistryEntry } from '@captain-tasko/shared';
import { WarningRow } from '../WarningRow.js';
import { RegistryTableHeader } from '../RegistryTableHeader.js';
import { SortableTh } from '../SortableTh.js';
import { useTableSort } from '../../lib/sort.js';
import { SourceCell } from './EventsTable.js';
import { coerceDateString, formatMarkerWhen, parseAuditMillis, DYNAMIC_TODAY } from './formatDate.js';

// ── Schema, mirrors registry/markers.yaml + future-events.yaml ──────
interface MarkerRenderConfig {
  enabled?: boolean;
  /** Short label rendered over the marker line/window on the viz. */
  label?: string;
  /** Hex color. */
  color?: string;
  width?: number;
  opacity?: number;
  /** SVG stroke-dasharray, or 'none'. */
  dasharray?: string;
  /** both | past | future */
  scope?: string;
  /** line | window */
  display?: string;
  /** Only present when display=window. */
  windowOpacity?: number;
}

interface TimelineMarker {
  id: string;
  name: string;
  /** Sentinel — distinguishes from regular events. */
  type?: string;
  /** ISO CE-only OR the special string 'dynamic:today' — the latter is
   *  a STRING, never parsed as a Date (the viz resolves it to
   *  `new Date()` at load time; this table renders the literal). */
  date: string;
  /** Optional — presence → shaded window instead of line. */
  endDate?: string;
  track?: string;
  cat?: string;
  location?: string;
  desc?: string;
  sourceLabel?: string;
  sourceUrl?: string;
  tags?: string[];
  /** Viz-rendering config sub-object — REQUIRED by the schema. */
  marker?: MarkerRenderConfig;
}

interface MarkersFile {
  schema_version?: number;
  events?: TimelineMarker[];
  [k: string]: unknown;
}

// ── CTSK-267: filter/sort plumbing ──────────────────────────────────

/** Effective display kind: explicit marker.display wins; otherwise
 *  endDate presence implies a window. Same rule as the Kind cell. */
function effectiveDisplay(m: TimelineMarker): 'line' | 'window' {
  return (m.marker?.display ?? (m.endDate ? 'window' : 'line')) === 'window' ? 'window' : 'line';
}

/** Epoch millis for sorting/timing. `dynamic:today` resolves to "now"
 *  — same semantics the viz applies at load time. */
function markerMillis(date: string): number | null {
  if (date === DYNAMIC_TODAY) return Date.now();
  return parseAuditMillis(date);
}

/** Timing bucket for the Future Windows filter, relative to now:
 *  upcoming (starts later), past (already ended), active (spanning
 *  now — or a dateless/unparseable row's safest bucket). */
function timingBucket(m: TimelineMarker, now: number): 'upcoming' | 'active' | 'past' {
  const start = markerMillis(m.date);
  const end = m.endDate ? markerMillis(m.endDate) : start;
  if (start != null && start > now) return 'upcoming';
  if (end != null && end < now) return 'past';
  return 'active';
}

// Fixed vocab from the marker schema (marker.scope).
const SCOPE_OPTIONS = ['both', 'past', 'future'] as const;
const DISPLAY_OPTIONS = ['line', 'window'] as const;
const TIMING_OPTIONS = ['upcoming', 'active', 'past'] as const;

// Sort accessors — module-level so the useTableSort memo key is
// referentially stable. When sorts numerically by epoch millis
// (dynamic:today = now).
const MARKER_SORT_ACCESSORS = {
  name:  (m: TimelineMarker) => m.name,
  kind:  (m: TimelineMarker) => effectiveDisplay(m),
  date:  (m: TimelineMarker) => markerMillis(m.date),
  scope: (m: TimelineMarker) => m.marker?.scope ?? null,
  label: (m: TimelineMarker) => m.marker?.label ?? null,
  tags:  (m: TimelineMarker) => (m.tags && m.tags.length > 0 ? m.tags.join(', ') : null),
  desc:  (m: TimelineMarker) => m.desc ?? null,
  source: (m: TimelineMarker) => m.sourceLabel ?? m.sourceUrl ?? null,
};

// ── Component ───────────────────────────────────────────────────────

export function TimelineMarkersTable({
  yamlText,
  variant = 'markers',
}: {
  yamlText: string;
  /** 'markers' → scope + display filters (markers.yaml); 'future' →
   *  track + timing filters (future-events.yaml, Future Windows tab
   *  via FutureWindowsTable). Same schema, schema-appropriate knobs. */
  variant?: 'markers' | 'future';
}) {
  const { entries, parseError } = useMemo<{
    entries: TimelineMarker[];
    parseError: string | null;
  }>(() => {
    if (!yamlText) return { entries: [], parseError: null };
    try {
      const parsed = yaml.load(yamlText) as MarkersFile | null;
      const list = ((parsed?.events ?? []) as TimelineMarker[]).map(normalizeMarker);
      return { entries: list, parseError: null };
    } catch (err) {
      return { entries: [], parseError: (err as Error).message };
    }
  }, [yamlText]);

  // ── CTSK-267: search / filter / sort ──────────────────────────────
  const [search, setSearch] = useState('');
  const [filterA, setFilterA] = useState(''); // markers: scope · future: track
  const [filterB, setFilterB] = useState(''); // markers: display · future: timing

  // Open-ended vocab (future variant's track dropdown) → from data.
  const trackOptions = useMemo(
    () => [...new Set(entries.map((m) => m.track).filter((t): t is string => !!t))].sort(),
    [entries],
  );

  const filtered = useMemo(() => {
    let rows = entries;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((m) => {
        const hay = [m.id, m.name, coerceDateString(m.date), m.track ?? '', m.cat ?? '', m.location ?? '', m.desc ?? '', m.marker?.label ?? '', m.marker?.scope ?? '', ...(m.tags ?? []), m.sourceLabel ?? '']
          .join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    const now = Date.now();
    if (variant === 'markers') {
      if (filterA) rows = rows.filter((m) => m.marker?.scope === filterA);
      if (filterB) rows = rows.filter((m) => effectiveDisplay(m) === filterB);
    } else {
      if (filterA) rows = rows.filter((m) => m.track === filterA);
      if (filterB) rows = rows.filter((m) => timingBucket(m, now) === filterB);
    }
    return rows;
  }, [entries, search, filterA, filterB, variant]);

  // Tri-state sortable columns; no default sort — this table's
  // pre-CTSK-267 order was the file's natural order.
  const { sorted, sort, setSort } = useTableSort(filtered, MARKER_SORT_ACCESSORS);

  const reset = () => {
    setSearch('');
    setFilterA('');
    setFilterB('');
    setSort(null);
  };

  const noun = variant === 'future' ? 'future windows' : 'markers';

  // Future Windows metric summary (brief §4): total · upcoming · past.
  const timingCounts = useMemo(() => {
    if (variant !== 'future') return null;
    const now = Date.now();
    const buckets = { upcoming: 0, active: 0, past: 0 };
    for (const m of entries) buckets[timingBucket(m, now)] += 1;
    return buckets;
  }, [entries, variant]);

  return (
    <div>
      {parseError && (
        <div role="alert" style={{ padding: 12, background: 'color-mix(in srgb, var(--danger-primary, #B3261E) 12%, var(--surface-raised))', border: '1px solid var(--danger-primary, #B3261E)', borderRadius: 'var(--radius-md)', color: 'var(--danger-primary, #B3261E)', fontSize: 13, marginBottom: 16 }}>
          <strong>YAML parse error:</strong> {parseError}
        </div>
      )}

      {entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
          No timeline markers yet — add entries with <code style={{ fontFamily: 'var(--font-mono)' }}>type: timeline-marker</code> and a{' '}
          <code style={{ fontFamily: 'var(--font-mono)' }}>marker: {'{}'}</code> sub-object under{' '}
          <code style={{ fontFamily: 'var(--font-mono)' }}>events:</code> in the file.
        </div>
      ) : (
        <>
          <RegistryTableHeader
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search name, label, track, tags, description…"
            filters={
              variant === 'markers'
                ? [
                    { label: 'All scopes', value: filterA, options: SCOPE_OPTIONS, onChange: setFilterA },
                    { label: 'All kinds', value: filterB, options: DISPLAY_OPTIONS, onChange: setFilterB },
                  ]
                : [
                    { label: 'All tracks', value: filterA, options: trackOptions, onChange: setFilterA },
                    { label: 'All timings', value: filterB, options: TIMING_OPTIONS, onChange: setFilterB },
                  ]
            }
            onReset={reset}
          />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            {sorted.length === entries.length
              ? `${entries.length} ${noun}`
              : `Showing ${sorted.length} of ${entries.length} ${noun}`}
            {timingCounts && (
              <> · {timingCounts.upcoming} upcoming · {timingCounts.active} active · {timingCounts.past} past</>
            )}
          </div>
          {sorted.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
              No {noun} match.
            </div>
          ) : (
          <table className="grid" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 200 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 210 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 170 }} />
              <col />
              <col style={{ width: 170 }} />
            </colgroup>
            <thead>
              <tr>
                <SortableTh sortKey="name" sort={sort} onSortChange={setSort}>Marker</SortableTh>
                <SortableTh sortKey="kind" sort={sort} onSortChange={setSort}>Kind</SortableTh>
                <SortableTh sortKey="date" sort={sort} onSortChange={setSort}>When</SortableTh>
                <SortableTh sortKey="scope" sort={sort} onSortChange={setSort}>Scope</SortableTh>
                <SortableTh sortKey="label" sort={sort} onSortChange={setSort}>Label</SortableTh>
                <SortableTh sortKey="tags" sort={sort} onSortChange={setSort}>Tags</SortableTh>
                <SortableTh sortKey="desc" sort={sort} onSortChange={setSort}>Description</SortableTh>
                <SortableTh sortKey="source" sort={sort} onSortChange={setSort}>Source</SortableTh>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m, idx) => {
                const isWindow = (m.marker?.display ?? (m.endDate ? 'window' : 'line')) === 'window';
                // CTSK-264 — read-time validation (same dispatcher as
                // the CTSK-255 write path, on the normalized entry).
                const validation = validateRegistryEntry('timeline-marker-v1', m);
                const cells = (
                  <>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.name}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, maxWidth: '100%' }}>
                        <span
                          title={m.marker?.color ?? ''}
                          style={{ display: 'inline-block', width: 11, height: 11, borderRadius: 3, background: m.marker?.color ?? 'var(--border-strong)', flexShrink: 0, opacity: m.marker?.opacity ?? 1 }}
                        />
                        <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
                      </span>
                    </td>
                    <td>
                      <span
                        style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 11, background: 'var(--surface-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}
                      >
                        {isWindow ? 'Window' : 'Line'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.date === DYNAMIC_TODAY ? DYNAMIC_TODAY : coerceDateString(m.date)}>
                      {isWindow ? formatMarkerWhen(m.date, m.endDate) : formatMarkerWhen(m.date, undefined)}
                    </td>
                    <td>
                      {m.marker?.scope ? (
                        <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 11, background: 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))', color: 'var(--accent-primary)' }}>
                          {m.marker.scope}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.marker?.label ?? ''}>
                      {m.marker?.label ?? <span style={{ color: 'var(--text-muted)', fontFamily: 'inherit' }}>—</span>}
                    </td>
                    <td>
                      {m.tags && m.tags.length > 0 ? (
                        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                          {m.tags.map((t) => (
                            <span
                              key={t}
                              style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11, background: 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))', color: 'var(--accent-primary)' }}
                            >
                              {t}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.desc ?? ''}>
                      {m.desc ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <SourceCell sourceLabel={m.sourceLabel} sourceUrl={m.sourceUrl} />
                    </td>
                  </>
                );
                const key = m.id ?? `row-${idx}`;
                return validation.ok
                  ? <tr key={key}>{cells}</tr>
                  : <WarningRow key={key} issues={validation.errors}>{cells}</WarningRow>;
              })}
            </tbody>
          </table>
          )}
        </>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

// `dynamic:today` is a plain string and must stay one; real ISO dates
// may resolve to JS Date objects depending on quoting — coerce both
// date fields to strings so nothing downstream calls Date methods on
// the sentinel or renders a raw Date. Tags coerce to strings (YAML
// scalars like `2038` would otherwise arrive as numbers).
function normalizeMarker(m: TimelineMarker): TimelineMarker {
  const out = { ...m };
  out.date = coerceDateString(out.date as unknown);
  if (out.endDate != null) out.endDate = coerceDateString(out.endDate as unknown);
  if (typeof out.desc === 'string') out.desc = out.desc.trim();
  if (Array.isArray(out.tags)) out.tags = out.tags.map((t) => String(t));
  return out;
}
